import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Box } from "@mui/material";
import SuiAlert from "components/SuiAlert";
import SuiBox from "components/SuiBox";
import ProcesosApi from "api/procesosApi";
import { styles } from "../styles";

const PATRON_CLASE = /\b(PRIMERA|SEGUNDA|TERCERA|CUARTA|QUINTA)\s+CLASE\b/gi;

/**
 * Advertencias (no bloquean la validación) cuando la propuesta de pago quedó desactualizada
 * respecto a las clases de las acreencias, por ejemplo al mover una acreencia de clase.
 */
export const obtenerAdvertenciasClases = async (procesoId, propuesta) => {
  const response = await ProcesosApi.obtenerDeudasProceso(procesoId);
  const deudas = response.data?.deudas || [];
  const clasesConAcreencias = new Set(
    deudas
      .map((deuda) => (deuda.str_clase_deuda?.nombre || "").trim().toUpperCase())
      .filter(Boolean)
  );
  const configuracion = propuesta?.configuracion || {};
  const clasesProyectadas = new Set(
    Object.keys(configuracion).map((clase) => clase.trim().toUpperCase())
  );
  const advertencias = [];
  clasesProyectadas.forEach((clase) => {
    if (!clasesConAcreencias.has(clase)) {
      advertencias.push(
        `La proyección tiene configurada la ${clase}, pero ya no hay acreencias en esa clase. ` +
          "Recalcule la proyección en el paso 5 (Finanzas)."
      );
    }
  });
  clasesConAcreencias.forEach((clase) => {
    if (clasesProyectadas.size > 0 && !clasesProyectadas.has(clase)) {
      advertencias.push(
        `Hay acreencias en la ${clase} que no están incluidas en la proyección. ` +
          "Recalcule la proyección en el paso 5 (Finanzas)."
      );
    }
  });
  const clasesEnTexto = new Set(
    (propuesta?.descripcion_general || "")
      .match(PATRON_CLASE)
      ?.map((clase) => clase.replace(/\s+/g, " ").toUpperCase()) || []
  );
  clasesEnTexto.forEach((clase) => {
    if (!clasesConAcreencias.has(clase)) {
      advertencias.push(
        `La descripción de la propuesta menciona la ${clase}, pero no hay acreencias en esa clase. ` +
          "Revise el texto en el paso 5 (Finanzas)."
      );
    }
  });
  return advertencias;
};

// Validador de proyección
export const validateProyeccion = async (procesoId) => {
  try {
    // Llamada a la API para obtener las propuestas del proceso
    const response = await ProcesosApi.obtenerPropuestas(procesoId, "solicitud");
    const proyeccionActiva = response.data[0];
    const mensajeError = proyeccionActiva ? "" : "El proceso no tiene una proyección de solicitud.";
    let advertencias = [];
    if (proyeccionActiva) {
      try {
        advertencias = await obtenerAdvertenciasClases(procesoId, proyeccionActiva);
      } catch (errorAdvertencias) {
        // Las advertencias son informativas: si no se pueden calcular no se bloquea la validación
        console.error("Error al revisar las clases de la proyección:", errorAdvertencias);
      }
    }
    return {
      completed: proyeccionActiva,
      results: {
        proyeccionActiva,
        mensajeError,
        advertencias,
      },
    };
  } catch (error) {
    console.error("Error al validar proyección:", error);
    return {
      completed: false,
      results: {
        proyeccionActiva: false,
        mensajeError: "Error al validar la proyección del proceso.",
      },
    };
  }
};

const VerificacionProyeccion = ({
  procesoId,
  onStepValidation = null,
  showResults = true,
  initialData = null,
}) => {
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [analisis, setAnalisis] = useState(initialData || { proyeccionActiva: false });

  useEffect(() => {
    const cargarProyeccion = async () => {
      if (initialData) {
        setAnalisis(initialData);
        if (onStepValidation) {
          onStepValidation(initialData.completed, initialData.results.mensajeError);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Validar la proyección usando el validador
        const resultado = await validateProyeccion(procesoId);
        setAnalisis(resultado);

        if (onStepValidation) {
          onStepValidation(resultado.completed, resultado.results.mensajeError);
        }
      } catch (error) {
        console.error("Error al cargar la proyección:", error);
        setError(obtenerMensajeError(error, "Error al cargar la proyección del proceso."));
        if (onStepValidation) {
          onStepValidation(false, "Error al cargar la proyección.");
        }
      } finally {
        setLoading(false);
      }
    };

    cargarProyeccion();
  }, [procesoId, onStepValidation, initialData]);

  if (loading) {
    return (
      <SuiBox sx={styles.loadingContainer}>
        <CircularProgress />
      </SuiBox>
    );
  }

  if (error) {
    return (
      <SuiBox sx={styles.errorContainer}>
        <SuiAlert color="error">{error}</SuiAlert>
      </SuiBox>
    );
  }

  if (!showResults) {
    return null;
  }

  return (
    <Box>
      {analisis.results.proyeccionActiva ? (
        <SuiAlert color="success">El proceso tiene una proyección activa.</SuiAlert>
      ) : (
        <SuiAlert color="error">{analisis.results.mensajeError}</SuiAlert>
      )}
      {(analisis.results.advertencias || []).map((advertencia) => (
        <SuiAlert key={advertencia} color="warning">
          {advertencia}
        </SuiAlert>
      ))}
    </Box>
  );
};

VerificacionProyeccion.propTypes = {
  procesoId: PropTypes.number.isRequired,
  onStepValidation: PropTypes.func,
  showResults: PropTypes.bool,
  initialData: PropTypes.object,
};

export default VerificacionProyeccion;
